import { z } from "zod";
import { useQuery } from "@tanstack/react-query";
import { LookupListPage } from "@/pages/admin/lookups/LookupListPage";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { apiFetch } from "@/lib/api";

interface Row { productFittingId: number; productTypeId: number; name: string; defaultCost: number | null; supplierId: number | null; note: string | null; deleted: boolean; }
interface ProductTypeOpt { productTypeId: number; name: string; }
interface SupplierOpt { id: number; name: string; }

const schema = z.object({
  productTypeId: z.number().int().positive(),
  name: z.string().min(1).max(200),
  defaultCost: z.number().nullable(),
  supplierId: z.number().int().nullable(),
  note: z.string().max(1000).nullable().optional(),
  deleted: z.boolean(),
});
type Form = z.infer<typeof schema>;

const asNumOrNull = (v: string) => (v === "" ? null : Number(v));

export function BomProductFittingsPage() {
  const { data: productTypes } = useQuery({ queryKey: ["product-types"], queryFn: () => apiFetch<ProductTypeOpt[]>("/api/product-types") });
  const { data: suppliers } = useQuery({ queryKey: ["suppliers"], queryFn: () => apiFetch<SupplierOpt[]>("/api/suppliers") });

  return <LookupListPage<Row, Form>
    title="Product fittings"
    apiPath="/api/lookups/bom-product-fittings"
    schema={schema}
    rowKey={(r) => r.productFittingId}
    columns={[
      { id: "name", header: "Name", accessor: (r) => r.name },
      { id: "type", header: "Product type", accessor: (r) => productTypes?.find((t) => t.productTypeId === r.productTypeId)?.name ?? String(r.productTypeId) },
      { id: "cost", header: "Default cost", accessor: (r) => (r.defaultCost != null ? `$${r.defaultCost.toFixed(2)}` : "—") },
    ]}
    toFormValues={(r) => ({ productTypeId: r.productTypeId, name: r.name, defaultCost: r.defaultCost, supplierId: r.supplierId, note: r.note ?? "", deleted: r.deleted })}
    toApiBody={(f) => ({
      productTypeId: f.productTypeId,
      name: f.name,
      defaultCost: f.defaultCost ?? null,
      supplierId: f.supplierId ? f.supplierId : null,
      note: f.note === "" ? null : (f.note ?? null),
      deleted: f.deleted,
    })}
    blankFormValues={{ productTypeId: 0, name: "", defaultCost: null, supplierId: null, note: "", deleted: false }}
    fields={(f) => (
      <div className="space-y-3">
        <div>
          <Label htmlFor="pt">Product type</Label>
          <select id="pt" className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
            {...f.register("productTypeId", { setValueAs: (v: string) => (v === "" ? 0 : Number(v)) })}>
            <option value="">— Select —</option>
            {(productTypes ?? []).map((t) => <option key={t.productTypeId} value={t.productTypeId}>{t.name}</option>)}
          </select>
        </div>
        <div><Label htmlFor="n">Name</Label><Input id="n" {...f.register("name")} /></div>
        <div><Label htmlFor="dc">Default cost</Label><Input id="dc" type="number" step="0.01" {...f.register("defaultCost", { setValueAs: asNumOrNull })} /></div>
        <div>
          <Label htmlFor="sup">Supplier</Label>
          <select id="sup" className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
            {...f.register("supplierId", { setValueAs: asNumOrNull })}>
            <option value="">— None —</option>
            {(suppliers ?? []).map((s) => <option key={s.id} value={s.id}>{s.name}</option>)}
          </select>
        </div>
        <div><Label htmlFor="note">Note</Label><Input id="note" {...f.register("note")} /></div>
        <Button type="submit">Save</Button>
      </div>
    )}
  />;
}
